/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, MoreVertical, Pencil, Copy, Trash2, Package, Settings2, ExternalLink, Link as LinkIcon } from "lucide-react";
import { usePackages, useDeletePackage, useTogglePackagePublished, useDuplicatePackage, useEventTypes, type PackageRow } from "@/hooks/usePackages";
import { EventTypeManagementSheet } from "@/components/EventTypeManagementSheet";
import { usePricingTypes, getUnitLabel } from "@/hooks/usePricingTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { publicPackageUrl } from "@/lib/public-urls";
import { toast } from "sonner";

type StatusFilter = "all" | "active" | "inactive";

export default function PackagesAdminContent() {
  const navigate = useNavigate();
  const { data: packages, isLoading } = usePackages();
  const { data: eventTypes } = useEventTypes();
  const { data: pricingTypes } = usePricingTypes();
  const deletePkg = useDeletePackage();
  const togglePublished = useTogglePackagePublished();
  const duplicatePkg = useDuplicatePackage();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<PackageRow | null>(null);
  const [eventTypeSheetOpen, setEventTypeSheetOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!packages) return [];
    let result = packages;
    if (statusFilter === "active") result = result.filter((p) => (p as any).is_published);
    if (statusFilter === "inactive") result = result.filter((p) => !(p as any).is_published);
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(s) || p.description?.toLowerCase().includes(s));
    }
    if (eventFilter !== "all") {
      result = result.filter((p) => p.event_types?.some((et) => et.event_type_id === eventFilter));
    }
    return result;
  }, [packages, search, statusFilter, eventFilter]);

  const formatPricing = (pkg: PackageRow) => {
    const unitLabel = getUnitLabel(pricingTypes, pkg.pricing_type);
    let text = `$${Number(pkg.price).toFixed(2)}/${unitLabel}`;
    if (pkg.min_guests || pkg.max_guests) {
      const parts = [];
      if (pkg.min_guests) parts.push(pkg.min_guests);
      if (pkg.max_guests) parts.push(pkg.max_guests);
      text += ` · ${parts.join("–")} guests`;
    }
    return text;
  };

  const counts = useMemo(() => {
    if (!packages) return { all: 0, active: 0, inactive: 0 };
    return {
      all: packages.length,
      active: packages.filter((p) => (p as any).is_published).length,
      inactive: packages.filter((p) => !(p as any).is_published).length,
    };
  }, [packages]);

  const copyLink = (slug: string | null) => {
    if (!slug) { toast.error("No slug set — edit the package first"); return; }
    navigator.clipboard.writeText(publicPackageUrl(slug));
    toast.success("Link copied!");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-muted-foreground text-sm">
          Bundle menu items into packages for weddings, baby showers, and other events.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={() => setEventTypeSheetOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" /> Event Types
          </Button>
          <Button onClick={() => navigate("/admin/packages/new")}>
            <Plus className="h-4 w-4 mr-2" /> Create Package
          </Button>
        </div>
      </div>

      {(packages?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <TabsList>
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="active">Published ({counts.active})</TabsTrigger>
              <TabsTrigger value="inactive">Draft ({counts.inactive})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex gap-3">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-full sm:w-[200px]" />
            </div>
            {(eventTypes?.length ?? 0) > 0 && (
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Events" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  {eventTypes?.map((et) => (
                    <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading...</div>
      ) : !packages?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Package className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-lg font-semibold mb-1">No packages yet</h3>
            <p className="text-muted-foreground text-sm max-w-md">Create packages that bundle multiple menu items together.</p>
            <Button className="mt-4" onClick={() => navigate("/admin/packages/new")}>
              <Plus className="h-4 w-4 mr-2" /> Create Your First Package
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                pricingLabel={formatPricing(pkg)}
                onEdit={() => navigate(`/admin/packages/${pkg.id}/edit`)}
                onDuplicate={() => duplicatePkg.mutate(pkg)}
                onDelete={() => setDeleteTarget(pkg)}
                onToggleActive={(checked) => togglePublished.mutate({ id: pkg.id, is_published: checked })}
                onCopyLink={() => copyLink((pkg as any).slug)}
              />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">No packages match your filters.</div>
          )}
        </>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteTarget) deletePkg.mutate(deleteTarget.id); setDeleteTarget(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EventTypeManagementSheet open={eventTypeSheetOpen} onOpenChange={setEventTypeSheetOpen} />
    </div>
  );
}

interface PackageCardProps {
  pkg: PackageRow;
  pricingLabel: string;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onToggleActive: (checked: boolean) => void;
  onCopyLink: () => void;
}

function PackageCard({ pkg, pricingLabel, onEdit, onDuplicate, onDelete, onToggleActive, onCopyLink }: PackageCardProps) {
  const isPublished = (pkg as any).is_published;
  const slug = (pkg as any).slug;

  return (
    <Card className="group hover:shadow-md transition-shadow cursor-pointer" onClick={onEdit}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-foreground truncate">{pkg.name}</h3>
            {slug ? (
              <a
                href={`/packages/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-muted-foreground hover:text-primary hover:underline transition-colors"
              >
                /packages/{slug} <ExternalLink className="inline h-3 w-3 ml-0.5" />
              </a>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
            <Switch checked={(pkg as any).is_published} onCheckedChange={onToggleActive} className="scale-90" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onDuplicate}>
                  <Copy className="h-4 w-4 mr-2" /> Duplicate
                </DropdownMenuItem>
                {slug && (
                  <>
                    <DropdownMenuItem onClick={onCopyLink}>
                      <LinkIcon className="h-4 w-4 mr-2" /> Copy Link
                    </DropdownMenuItem>
                    {isPublished && (
                      <DropdownMenuItem asChild>
                        <a href={`/packages/${slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center">
                          <ExternalLink className="h-4 w-4 mr-2" /> View Public Page
                        </a>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {pkg.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{pkg.description}</p>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>{pricingLabel}</span>
          <div className="flex items-center gap-2">
            {!(pkg as any).show_prices && (
              <Badge variant="outline" className="text-xs text-muted-foreground">Prices Hidden</Badge>
            )}
            <Badge variant={isPublished ? "default" : "secondary"} className="text-xs">
              {isPublished ? "Published" : "Draft"}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
